import React, { useEffect, useLayoutEffect, useRef } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { useSearch } from "../../../contexts/SearchContext";
import {
  bookingStepFromFlightUrlSegment,
  buildFlightFlowDashboardPath,
  buildSearchOfferDashboardPath,
  logFlightsBackToCategoriesNavigation,
  scrollSearchOfferFlightFlowToTop,
} from "../flightBookingPathUtils";
import { useFlightOfferUi } from "../../../contexts/FlightOfferUiContext";
import { peekMainFlightSearchEditorRequest } from "../../../features/searchOffer/searchOfferEditSearchHandshake";
import { useBookingFlow } from "./hooks/useBookingFlow";
import { shouldForceWizardBackToResults } from "./wizard/flightPathSyncPolicy";

const FLIGHT_STEP_ORDER = [
  "search",
  "results",
  "selfComposePick",
  "flightDetails",
  "passengers",
  "extras",
  "review",
  "holdConfirmation",
  "draftPayment",
  "success",
];
const DEBUG_ENDPOINT =
  "http://127.0.0.1:7626/ingest/ca460569-320b-4741-be20-10cc54aa7608";

function getStepIndex(step) {
  const idx = FLIGHT_STEP_ORDER.indexOf(step);
  return idx >= 0 ? idx : -1;
}

function isDowngrade(targetStep, currentStep) {
  const targetIdx = getStepIndex(targetStep);
  const currentIdx = getStepIndex(currentStep);
  if (targetIdx < 0 || currentIdx < 0) return false;
  return targetIdx < currentIdx;
}

function postDebugLog({ runId, hypothesisId, location, message, data }) {
  return;
}

function extractFlightStepSegmentFromPathname(pathname) {
  if (typeof pathname !== "string") return null;
  const m = pathname.match(/\/flights\/([^/?#]+)/);
  return m?.[1] || null;
}

/**
 * מסנכרן שלב זרימת הטיסות עם URL: .../searchoffer/flights/:flightStep
 * (רק תחת /searchoffer, לא בדף mockup עצמאי).
 */
export default function FlightBookingPathSync() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const params = useParams();
  const { isSearched, searchTab } = useSearch();
  const { currentStep, goToStep, openResultsAfterPlanesSearch } = useBookingFlow();
  const { setFlightSearchEditorOpen } = useFlightOfferUi();

  const lastPushedRef = useRef(null);
  const prevUrlFlightStepRef = useRef(undefined);
  /** נכון כשהיה מקטע ‎/flights/‎ ב-URL — לזיהוי מעבר חזרה לבסיס searchoffer */
  const prevHadFlightSegmentInUrlRef = useRef(false);

  const flightStepParam = params.flightStep;
  const flightStepParamFromPathname =
    extractFlightStepSegmentFromPathname(location.pathname) ||
    extractFlightStepSegmentFromPathname(
      typeof window !== "undefined" ? window.location.pathname || "" : ""
    );
  const effectiveFlightStepParam = flightStepParam || flightStepParamFromPathname;
  const { selectedPackageId, dynamicOrderId, hotelOrderId } = params;

  const onSearchOffer =
    typeof location.pathname === "string" &&
    location.pathname.includes("/searchoffer");
  const dev =
    typeof import.meta !== "undefined" && import.meta.env?.DEV === true;

  /**
   * מקטע ‎/flights/:step‎ — גלילה לראש.
   * ללא מקטע flights — סנכרון דטרמיניסטי: שלב wizard חוזה ל־search + ניקוי lastPushed
   * (תיקון דסינכרון אחרי חזרה ל־/searchoffer כש־lastPushedRef חוסם navigate והשלב נשאר results).
   */
  useLayoutEffect(() => {
    if (!onSearchOffer) return;
    if (effectiveFlightStepParam) {
      prevHadFlightSegmentInUrlRef.current = true;
      scrollSearchOfferFlightFlowToTop();
      const id = requestAnimationFrame(() => {
        scrollSearchOfferFlightFlowToTop();
      });
      return () => cancelAnimationFrame(id);
    }

    prevUrlFlightStepRef.current = undefined;
    setFlightSearchEditorOpen(false);
    lastPushedRef.current = null;

    if (currentStep === "search") {
      prevHadFlightSegmentInUrlRef.current = false;
      return;
    }

    const transitionedOffFlightUrl = prevHadFlightSegmentInUrlRef.current === true;
    prevHadFlightSegmentInUrlRef.current = false;

    if (currentStep !== "search") {
      goToStep("search");
    } else if (dev) {
      console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
        pathname: location.pathname,
        currentStep,
        nextPath: null,
        action: "skip_same_step",
        targetStep: "search",
      });
    }

    if (import.meta.env.DEV && transitionedOffFlightUrl) {
      logFlightsBackToCategoriesNavigation({
        fromPath: location.pathname,
        targetPath: buildSearchOfferDashboardPath({
          selectedPackageId,
          dynamicOrderId,
          hotelOrderId,
        }),
        searchTabBefore: searchTab,
        searchTabAfter: searchTab,
        reason: "searchoffer_url_without_flights_segment_sync_step_search",
      });
    }
  }, [
    onSearchOffer,
    effectiveFlightStepParam,
    currentStep,
    goToStep,
    setFlightSearchEditorOpen,
    location.pathname,
    searchTab,
    selectedPackageId,
    dynamicOrderId,
    hotelOrderId,
  ]);

  /**
   * כתובת …/flights/results + isSearched: קידום לשלב results לפני paint — מונע מצב שבו immersive כבר true
   * אבל FlightWizardShell עדיין ב-phase search (תוכן ב־.fw-search-phase-slot { display:none }) ורשימת טיסות לא מוצגת.
   */
  useLayoutEffect(() => {
    if (!onSearchOffer || effectiveFlightStepParam !== "results") return;
    if (peekMainFlightSearchEditorRequest()) return;
    const step = bookingStepFromFlightUrlSegment(effectiveFlightStepParam);
    if (step !== "results") return;
    if (!isSearched) return;
    if (currentStep !== "search") return;
    setFlightSearchEditorOpen(false);
    prevUrlFlightStepRef.current = effectiveFlightStepParam;
    openResultsAfterPlanesSearch();
  }, [
    onSearchOffer,
    effectiveFlightStepParam,
    isSearched,
    currentStep,
    openResultsAfterPlanesSearch,
    setFlightSearchEditorOpen,
  ]);

  /** URL השתנה (כולל חזור אחורה) → עדכון שלב */
  useEffect(() => {
    if (!onSearchOffer) return;
    if (peekMainFlightSearchEditorRequest()) return;
    const windowPathname =
      typeof window !== "undefined" ? window.location.pathname || "" : "";
    if (
      ((typeof location.pathname === "string" &&
        location.pathname.includes("/flights/results")) ||
        windowPathname.includes("/flights/results")) &&
      currentStep === "results"
    ) {
      if (dev) {
        console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
          pathname: location.pathname,
          currentStep,
          nextPath: null,
          action: "skip_same_step",
          targetStep: "results",
          reason: "results_path_and_step_already_results",
        });
      }
      return;
    }

    // #region agent log
    postDebugLog({
      runId: "run2",
      hypothesisId: "H4",
      location: "FlightBookingPathSync.jsx:176",
      message: "[FLIGHT_PATHSYNC_URL_EFFECT]",
      data: {
        action: "effect_enter",
        pathname: location.pathname,
        flightStepParam: flightStepParam ?? null,
        flightStepParamFromPathname: flightStepParamFromPathname ?? null,
        effectiveFlightStepParam: effectiveFlightStepParam ?? null,
        prevSeg: prevUrlFlightStepRef.current ?? null,
        currentStep,
        isSearched,
        navigationType,
      },
    });
    // #endregion

    if (!effectiveFlightStepParam) {
      if (dev) {
        console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
          pathname: location.pathname,
          currentStep,
          nextPath: null,
          action: "skip",
          reason: "missing_flightStepParam",
        });
      }
      return;
    }

    if (prevUrlFlightStepRef.current === effectiveFlightStepParam) {
      if (dev) {
        console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
          pathname: location.pathname,
          currentStep,
          nextPath: null,
          action: "skip",
          reason: "same_step_param_as_prev",
          flightStepParam: effectiveFlightStepParam,
        });
      }
      return;
    }
    const prevSeg = prevUrlFlightStepRef.current;

    const step = bookingStepFromFlightUrlSegment(effectiveFlightStepParam);
    if (!step) {
      if (dev) {
        console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
          pathname: location.pathname,
          currentStep,
          nextPath: null,
          action: "skip",
          reason: "unmapped_path_segment",
          flightStepParam: effectiveFlightStepParam,
        });
      }
      return;
    }

    const targetStep = step;
    const downgrade = isDowngrade(targetStep, currentStep);
    const allowWizardBackToList =
      (currentStep === "flightDetails" || currentStep === "selfComposePick") &&
      (targetStep === "results" || targetStep === "selfComposePick");
    const allowDowngrade =
      navigationType === "POP" ||
      currentStep === "search" ||
      allowWizardBackToList;
    if (downgrade && !allowDowngrade) {
      prevUrlFlightStepRef.current = effectiveFlightStepParam;
      if (dev) {
        console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
          action: "skip_downgrade",
          pathname: location.pathname,
          fromStep: currentStep,
          targetStep,
          navigationType,
        });
      }
      // #region agent log
      postDebugLog({
        runId: "run1",
        hypothesisId: "H5",
        location: "FlightBookingPathSync.jsx:244",
        message: "[FLIGHT_PATH_SYNC_AUDIT]",
        data: {
          action: "skip_downgrade",
          pathname: location.pathname,
          fromStep: currentStep,
          targetStep,
          navigationType,
        },
      });
      // #endregion
      return;
    }

    if (step === "results") {
      // #region agent log
      postDebugLog({
        runId: "run2",
        hypothesisId: "H4",
        location: "FlightBookingPathSync.jsx:269",
        message: "[FLIGHT_PATHSYNC_RESULTS_BRANCH]",
        data: {
          action: "results_branch_enter",
          pathname: location.pathname,
          currentStep,
          isSearched,
          prevSeg: prevSeg ?? null,
          navigationType,
        },
      });
      // #endregion
      if (!isSearched) return;
      if (currentStep === "results") {
        prevUrlFlightStepRef.current = effectiveFlightStepParam;
        setFlightSearchEditorOpen(false);
        if (dev) {
          console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
            pathname: location.pathname,
            currentStep,
            nextPath: null,
            action: "skip_same_step",
            targetStep: "results",
            reason: "results_path_already_hydrated",
          });
        }
        return;
      }
      if (currentStep !== "search" && currentStep !== "results") {
        if (
          shouldForceWizardBackToResults({
            urlStep: "results",
            currentStep,
            navigationType,
          })
        ) {
          prevUrlFlightStepRef.current = effectiveFlightStepParam;
          setFlightSearchEditorOpen(false);
          goToStep("results");
          return;
        }
        if (dev) {
          console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
            pathname: location.pathname,
            currentStep,
            nextPath: null,
            action: "skip",
            reason: "avoid_downgrade_from_advanced_step",
            hydrateStep: "results",
          });
        }
        return;
      }
      prevUrlFlightStepRef.current = effectiveFlightStepParam;
      setFlightSearchEditorOpen(false);
      // #region agent log
      postDebugLog({
        runId: "run1",
        hypothesisId: "H5",
        location: "FlightBookingPathSync.jsx:287",
        message: "[FLIGHT_RESULTS_BACK_NAV_SOURCE]",
        data: {
          sourceFile: "FlightBookingPathSync.jsx",
          action: "hydrate_from_path",
          fromStep: currentStep,
          targetStep: "results",
          pathnameBefore: location.pathname,
          nextPath: null,
          reason: prevSeg === undefined ? "first_segment_results" : "url_results_change",
          navigationType,
        },
      });
      // #endregion
      if (dev) {
        console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
          pathname: location.pathname,
          currentStep,
          nextPath: null,
          action: "hydrate_from_path",
          hydrateStep: "results",
          prevSeg: prevSeg ?? null,
        });
      }
      if (prevSeg === undefined) {
        openResultsAfterPlanesSearch();
      } else {
        if (currentStep === "results") {
          if (dev) {
            console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
              pathname: location.pathname,
              currentStep,
              nextPath: null,
              action: "skip_same_step",
              targetStep: "results",
            });
          }
        } else {
          goToStep("results");
        }
      }
      return;
    }

    if (step === "search" && isSearched) {
      if (currentStep !== "search" && currentStep !== "results") {
        if (dev) {
          console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
            pathname: location.pathname,
            currentStep,
            nextPath: null,
            action: "skip",
            reason: "search_path_while_step_already_advanced",
            hydrateStep: "results",
          });
        }
        return;
      }
      prevUrlFlightStepRef.current = effectiveFlightStepParam;
      setFlightSearchEditorOpen(true);
      if (dev) {
        console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
          pathname: location.pathname,
          currentStep,
          nextPath: null,
          action: "hydrate_from_path",
          hydrateStep: "search",
          prevSeg: prevSeg ?? null,
        });
      }
      if (prevSeg === undefined) {
        openResultsAfterPlanesSearch();
      } else {
        if (currentStep === "results") {
          if (dev) {
            console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
              pathname: location.pathname,
              currentStep,
              nextPath: null,
              action: "skip_same_step",
              targetStep: "results",
            });
          }
        } else {
          goToStep("results");
        }
      }
      return;
    }

    prevUrlFlightStepRef.current = effectiveFlightStepParam;
    setFlightSearchEditorOpen(false);
    if (dev) {
      console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
        pathname: location.pathname,
        currentStep,
        nextPath: null,
        action: "hydrate_from_path",
        hydrateStep: step,
      });
    }
    if (step === currentStep) {
      if (dev) {
        console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
          pathname: location.pathname,
          currentStep,
          nextPath: null,
          action: "skip_same_step",
          targetStep: step,
        });
      }
      return;
    }
    goToStep(step);
  }, [
    onSearchOffer,
    flightStepParam,
    flightStepParamFromPathname,
    effectiveFlightStepParam,
    isSearched,
    currentStep,
    navigationType,
    location.pathname,
    dev,
    goToStep,
    openResultsAfterPlanesSearch,
    setFlightSearchEditorOpen,
  ]);

  /** שלב השתנה → עדכון URL */
  useEffect(() => {
    if (!onSearchOffer) return;
    /**
     * Edit Search asked for the main search-offer surface. currentStep is still
     * "results" for this commit, and syncing it would replace the navigation
     * back to /flights/results while the editor handshake hides both the form
     * and the cards (blank page).
     */
    if (peekMainFlightSearchEditorRequest()) {
      const base = buildSearchOfferDashboardPath({
        selectedPackageId,
        dynamicOrderId,
        hotelOrderId,
      });
      if (currentStep !== "search") {
        goToStep("search");
      }
      if (location.pathname !== base) {
        lastPushedRef.current = base;
        navigate(base, { replace: true });
      }
      return;
    }
    const windowPathname =
      typeof window !== "undefined" ? window.location.pathname || "" : "";
    if (
      ((typeof location.pathname === "string" &&
        location.pathname.includes("/flights/results")) ||
        windowPathname.includes("/flights/results")) &&
      currentStep === "results"
    ) {
      return;
    }

    const pathParams = { selectedPackageId, dynamicOrderId, hotelOrderId };
    const target = buildFlightFlowDashboardPath(pathParams, currentStep);
    if (location.pathname !== target) {
      lastPushedRef.current = null;
    }
    if (location.pathname === target) {
      lastPushedRef.current = target;
      if (dev) {
        console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
          pathname: location.pathname,
          currentStep,
          nextPath: target,
          action: "skip",
          reason: "already_on_target_path",
        });
      }
      return;
    }
    if (lastPushedRef.current === target) {
      if (dev) {
        console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
          pathname: location.pathname,
          currentStep,
          nextPath: target,
          action: "skip",
          reason: "target_already_pushed",
        });
      }
      return;
    }

    lastPushedRef.current = target;
    const reason =
      target.includes("/flights/results")
        ? "sync_currentStep_to_results_path"
        : "sync_currentStep_to_step_path";
    // #region agent log
    postDebugLog({
      runId: "run1",
      hypothesisId: "H5",
      location: "FlightBookingPathSync.jsx:413",
      message: "[FLIGHT_PATH_SYNC_AUDIT]",
      data: {
        action: "navigate",
        currentStep,
        nextPath: target,
        pathname: location.pathname,
        navigationType,
        reason,
      },
    });
    // #endregion
    if (target.includes("/flights/results")) {
      // #region agent log
      postDebugLog({
        runId: "run1",
        hypothesisId: "H5",
        location: "FlightBookingPathSync.jsx:430",
        message: "[FLIGHT_RESULTS_BACK_NAV_SOURCE]",
        data: {
          sourceFile: "FlightBookingPathSync.jsx",
          action: "navigate_replace",
          fromStep: currentStep,
          targetStep: "results",
          pathnameBefore: location.pathname,
          nextPath: target,
          reason,
          navigationType,
        },
      });
      // #endregion
    }
    if (dev) {
      console.log("[FLIGHT_PATH_SYNC_AUDIT]", {
        pathname: location.pathname,
        currentStep,
        nextPath: target,
        action: "navigate",
        navigationType,
        reason,
      });
    }
    navigate(target, { replace: true });
  }, [
    onSearchOffer,
    currentStep,
    navigate,
    location.pathname,
    effectiveFlightStepParam,
    dev,
    selectedPackageId,
    dynamicOrderId,
    hotelOrderId,
  ]);

  return null;
}
